import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, Clock, Users, Zap, Copy, ExternalLink, Search, Filter, Shield, Eye } from 'lucide-react';

interface Block {
  number: number;
  hash: string;
  timestamp: string;
  transactions: number;
  gasUsed: number;
  gasLimit: number;
  validator: string;
  validatorName?: string;
  validatorAddress: string;
  size: number;
  difficulty: string;
  reward: number;
}

const BlocksPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [filteredBlocks, setFilteredBlocks] = useState<Block[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [validatorFilter, setValidatorFilter] = useState(searchParams.get('validator') || '');
  const blocksPerPage = 20;

  useEffect(() => {
    const fetchBlocks = async () => {
      setLoading(true);
      
      try {
        // 실제 API 호출
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
        const response = await fetch(`${apiBaseUrl}/explorer/blocks?limit=100`);
        
        let blocksData: Block[] = [];
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.blocks) {
            blocksData = data.blocks.map((block: any) => ({
              number: block.number || 0,
              hash: block.hash || '0x0',
              timestamp: block.timestamp ? new Date(block.timestamp * 1000).toISOString() : new Date().toISOString(),
              transactions: block.transactionCount || block.transactions?.length || 0,
              gasUsed: parseInt(block.gasUsed || '0'),
              gasLimit: parseInt(block.gasLimit || '0'),
              validator: block.miner || block.validator || null,
              validatorName: block.miner || block.validator ? 'Validator' : 'Unknown',
              validatorAddress: block.miner || block.validator || null,
              size: block.size || 0,
              difficulty: '0',
              reward: parseFloat(block.reward || '2.0')
            }));
          }
        }
        
        // API 실패시 DB에서 조회 시도
        if (blocksData.length === 0) {
          try {
            const dbResponse = await fetch(`${apiBaseUrl}/blockchain/blocks?limit=100`);
            if (dbResponse.ok) {
              const dbData = await dbResponse.json();
              if (dbData.success && dbData.blocks) {
                blocksData = dbData.blocks.map((block: any) => ({
                  number: block.number || 0,
                  hash: block.hash || '0x0',
                  timestamp: block.timestamp ? new Date(block.timestamp).toISOString() : new Date().toISOString(),
                  transactions: block.transactions?.length || 0,
                  gasUsed: parseInt(block.gasUsed || '0'),
                  gasLimit: parseInt(block.gasLimit || '0'),
                  validator: block.miner || block.validator || null,
                  validatorName: block.miner || block.validator ? 'Validator' : 'Unknown',
                  validatorAddress: block.miner || block.validator || null,
                  size: block.size || 0,
                  difficulty: '0',
                  reward: parseFloat(block.reward || '2.0')
                }));
              }
            }
          } catch (dbError) {
            console.warn('DB 조회 실패:', dbError);
          }
        }
        
        // Validator 필터 적용
        if (validatorFilter) {
          blocksData = blocksData.filter(block => 
            block.validatorAddress.toLowerCase() === validatorFilter.toLowerCase()
          );
        }
        
        setBlocks(blocksData);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch blocks:', error);
        setLoading(false);
      }
    };

    fetchBlocks();
    const interval = setInterval(fetchBlocks, 10000); // 10초마다 업데이트
    return () => clearInterval(interval);
  }, [validatorFilter]);

  // 필터링 로직
  useEffect(() => {
    let filtered = blocks;
    
    if (searchTerm) {
      filtered = filtered.filter(block => 
        block.number.toString().includes(searchTerm) ||
        block.hash.toLowerCase().includes(searchTerm.toLowerCase()) ||
        block.validator.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (block.validatorName && block.validatorName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (validatorFilter) {
      filtered = filtered.filter(block => 
        block.validatorAddress === validatorFilter ||
        block.validator === validatorFilter ||
        (block.validatorName && block.validatorName.toLowerCase().includes(validatorFilter.toLowerCase()))
      );
    }
    
    setFilteredBlocks(filtered);
    setCurrentPage(1); // 필터 변경 시 첫 페이지로
  }, [blocks, searchTerm, validatorFilter]);

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const paginatedBlocks = filteredBlocks.slice(
    (currentPage - 1) * blocksPerPage,
    currentPage * blocksPerPage
  );

  const totalPages = Math.ceil(filteredBlocks.length / blocksPerPage);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Blocks</h1>
        </div>
        <div className="card">
          <div className="space-y-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blocks</h1>
          <p className="text-gray-600 mt-2">Latest blocks on the Voltron Mainnet</p>
          {validatorFilter && (
            <p className="text-sm text-blue-600 mt-1">
              필터링됨: {validatorFilter}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 px-3 py-1 bg-green-50 border border-green-200 rounded-lg">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-green-800">Live</span>
          </div>
          <span className="text-sm text-gray-600">
            {filteredBlocks.length.toLocaleString()} blocks
          </span>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="블록 번호, 해시, 검증자로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex space-x-3">
            <input
              type="text"
              placeholder="검증자 주소 또는 이름으로 필터링"
              value={validatorFilter}
              onChange={(e) => setValidatorFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            {validatorFilter && (
              <button
                onClick={() => setValidatorFilter('')}
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                필터 제거
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Latest Block</p>
              <p className="text-2xl font-bold text-gray-900">{blocks[0]?.number.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Block Time</p>
              <p className="text-2xl font-bold text-gray-900">12s</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Clock className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Gas Used</p>
              <p className="text-2xl font-bold text-gray-900">7.5M</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Zap className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Transactions</p>
              <p className="text-2xl font-bold text-gray-900">150</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Blocks Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Block</th>
                <th>Age</th>
                <th>Transactions</th>
                <th>Gas Used</th>
                <th>Gas Limit</th>
                <th>Validator</th>
                <th>Reward</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBlocks.map((block) => (
                <tr key={block.number} className="hover:bg-gray-50">
                  <td>
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Activity className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <Link 
                          to={`/block/${block.number}`}
                          className="font-medium text-blue-600 hover:text-blue-800"
                        >
                          #{block.number.toLocaleString()}
                        </Link>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-sm text-gray-500 font-mono">
                            {block.hash.substring(0, 10)}...{block.hash.slice(-8)}
                          </span>
                          <button
                            onClick={() => copyToClipboard(block.hash)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {formatTimeAgo(block.timestamp)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className="inline-flex items-center px-2 py-1 rounded-lg text-sm font-medium bg-green-50 text-green-700">
                      {block.transactions}
                    </span>
                  </td>
                  <td>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {(block.gasUsed / 1000000).toFixed(2)}M
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div 
                          className="bg-orange-500 h-2 rounded-full" 
                          style={{ width: `${(block.gasUsed / block.gasLimit) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="text-sm text-gray-600">
                      {(block.gasLimit / 1000000).toFixed(1)}M
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-2">
                        <Shield className="w-4 h-4 text-purple-500" />
                        <div>
                          {block.validatorName ? (
                            <button
                              onClick={() => navigate(`/validators?validator=${block.validatorAddress}`)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800 underline"
                            >
                              {block.validatorName}
                            </button>
                          ) : (
                            <button
                              onClick={() => navigate(`/validators?validator=${block.validatorAddress}`)}
                              className="text-sm font-mono text-blue-600 hover:text-blue-800 underline"
                            >
                              {block.validator.slice(0, 12)}...
                            </button>
                          )}
                          <div className="text-xs text-gray-500 font-mono">
                            {block.validatorAddress.slice(0, 6)}...{block.validatorAddress.slice(-4)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/validators?validator=${block.validatorAddress}`)}
                        className="text-gray-400 hover:text-gray-600"
                        title="검증자 상세 정보 보기"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className="text-sm font-medium text-green-600">
                      {block.reward.toFixed(4)} VTTN
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {(currentPage - 1) * blocksPerPage + 1} to {Math.min(currentPage * blocksPerPage, filteredBlocks.length)} of {filteredBlocks.length} blocks
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm font-medium">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlocksPage;